import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Router from 'next/router';
import queryString from 'query-string';

import PageContainer from '@deliveroo/consumer-component-library/components/PageContainer';
import Heading from '@deliveroo/consumer-component-library/components/Heading';
import Spacer from '@deliveroo/consumer-component-library/components/Spacer';
import Button from '@deliveroo/consumer-component-library/components/Button';
import RestaurantCardHome from '../../components/RestaurantCardHome';
import Carousel from '../../components/Carousel';
import Slide from '../../components/Slide';
import i18n from '../../utils/i18n';
import { shortAddress } from '../../utils/address';
import { navigateToSavedAddress } from '../../actions/locationActions';

import styles from './styles.scss';

const MAX_ADDRESSES = 2;

export class CuisineRestaurantsContainer extends React.Component {
  onClickViewAll(addressId) {
    this.props.navigateToSavedAddress({ id: addressId }).then(() => {
      this.navigateToRedirectUrl();
    });
  }

  navigateToRedirectUrl() {
    const { restaurantsRedirectLocation, hasClientSideRouting, cuisine } = this.props;

    if (restaurantsRedirectLocation) {
      const [pathname, search] = restaurantsRedirectLocation.split('?');

      // We slice to ignore the first slash
      const pathParts = pathname.slice(1).split('/');
      const queryParams = {
        ...queryString.parse(search),
        city: pathParts[1],
        neighborhood: pathParts[2],
      };

      const restaurantsRedirectLocationWithFilter = `${restaurantsRedirectLocation}&tags=${cuisine.toLowerCase()}`;

      if (hasClientSideRouting) {
        Router.push(
          { pathname: '/restaurants', query: queryParams },
          restaurantsRedirectLocationWithFilter,
        );
      } else {
        window.location = restaurantsRedirectLocationWithFilter;
      }
    }
  }

  render() {
    const { recentAddresses, tld, isPostcodeSearch } = this.props;

    const viewAll = (addressId) => (
      <Spacer spacing="0 medium">
        <Button onClick={() => this.onClickViewAll(addressId)} size="small" type="tertiary">
          <span className={styles.viewAll}>
            {i18n.gettext('landingPage.cuisine.restaurants.viewAll')}
          </span>
        </Button>
      </Spacer>
    );

    if (
      recentAddresses.length === 0 ||
      recentAddresses.every(({ restaurants }) => restaurants.length === 0)
    ) {
      return null;
    }

    return (
      <div className={styles.outerWrapper}>
        <PageContainer>
          <PageContainer fullWidth="mobile-only">
            <div className={styles.innerWrapper}>
              {recentAddresses.slice(0, MAX_ADDRESSES).map(({ address, restaurants }) => {
                if (!restaurants || restaurants.length === 0) {
                  return null;
                }
                const { id, postcode, neighborhood } = address;

                return (
                  <Spacer adjacentVerticalSpacing="x-large" key={`wrapper-${postcode}`}>
                    <Spacer spacing="small 0 large 0">
                      <div className={styles.heading}>
                        <Heading level={3} color="black" size="x-small">
                          <span className={styles.headerText}>
                            {i18n.gettext('landingPage.cuisine.restaurants.header(%(location)s)', {
                              location: shortAddress({
                                tld,
                                isPostcodeSearch,
                                postcode,
                                neighborhood,
                              }),
                            })}
                          </span>
                        </Heading>
                        {viewAll(id)}
                      </div>
                    </Spacer>
                    <Carousel id={`carousel-${postcode}`}>
                      {restaurants.map((restaurant) => (
                        <Slide key={`${postcode}-${restaurant.id}`} className={styles.card}>
                          <RestaurantCardHome cardSize="MEDIUM" {...restaurant} />
                        </Slide>
                      ))}
                    </Carousel>
                  </Spacer>
                );
              })}
            </div>
          </PageContainer>
        </PageContainer>
      </div>
    );
  }
}

CuisineRestaurantsContainer.propTypes = {
  cuisine: PropTypes.string.isRequired,
  recentAddresses: PropTypes.arrayOf(
    PropTypes.shape({
      address: PropTypes.shape({
        id: PropTypes.string,
        postcode: PropTypes.string,
      }).isRequired,
      restaurants: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          imageUrl: PropTypes.string.isRequired,
        }),
      ),
    }),
  ),
  restaurantsRedirectLocation: PropTypes.string,
  hasClientSideRouting: PropTypes.bool,
  navigateToSavedAddress: PropTypes.func.isRequired,
  tld: PropTypes.string.isRequired,
  isPostcodeSearch: PropTypes.bool.isRequired,
};

CuisineRestaurantsContainer.defaultProps = {
  recentAddresses: [],
  restaurantsRedirectLocation: undefined,
  hasClientSideRouting: false,
};

const mapStateToProps = (state) => ({
  cuisine: state.cuisine.cuisine,
  recentAddresses: state.cuisine.restaurants ? state.cuisine.restaurants.data : [],
  restaurantsRedirectLocation: state.location.search.restaurantsRedirectLocation,
  hasClientSideRouting: state.config.experiments.mvt_1066_placeholders_web === 'placeholders',
  tld: state.request.country.tld,
  isPostcodeSearch: state.request.country.postcodeSearch,
});

const mapDispatchToProps = (dispatch) => ({
  navigateToSavedAddress: (options) => dispatch(navigateToSavedAddress(options)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CuisineRestaurantsContainer);
